import {
  Children,
  Fragment,
  isValidElement,
  memo,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type JSX,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import { DotMenuIcon } from '@dynatrace/strato-icons';

import * as styles from './RowActions.sty.js';
import { Button } from '../../../../buttons/button/Button.js';
import { Menu, type MenuProps } from '../../../../navigation/menu/Menu.js';
import { isTrigger as isMenuTrigger } from '../../../../navigation/menu/Trigger.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { DATA_TABLE_ROW_ACTIONS_COLUMN_ID } from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import { type ColumnDef } from '../../hooks/useTable/types.js';
import { dataTableMessages } from '../../messages.js';
import { type DataTableRowData } from '../../public.api.js';

/**
 * Props for the `DataTableRowActions` component.
 * @public
 */
export interface DataTableRowActionsProps<TData extends DataTableRowData> {
  children: (
    row: TData,
    {
      rowDensity,
      rowIndex,
      rowId,
    }: {
      rowDensity: 'default' | 'condensed' | 'comfortable';
      rowIndex: number;
      rowId: string;
    },
  ) => ReactElement<PropsWithChildren>;
}

/**
 * The slot component for defining row actions within the DataTable.
 * @public
 */
export function DataTableRowActions<TData extends DataTableRowData>(
  props: DataTableRowActionsProps<TData>,
) {
  return null;
}

/**
 * Typeguard function that will check if a ReactNode is of type DataTableRowActions.
 * @internal
 */
export function isDataTableRowActions<TData extends DataTableRowData>(
  child: ReactNode,
): child is ReactElement<DataTableRowActionsProps<TData>> {
  if (isElement(child) && child.type === DataTableRowActions) {
    return true;
  }
  return false;
}

/**
 * Internal wrapper component for the row actions children.
 * @internal
 */
const DataTableInternalRowActions = memo(
  (props: PropsWithChildren<{ rowId: string }>) => {
    const table = useDataTable();
    const intl = useIntl();
    const density =
      table.getVisualVariantOptions().rowDensity === 'condensed'
        ? 'condensed'
        : 'default';
    const ariaLabel = intl.formatMessage(dataTableMessages.rowActionsTrigger);

    const [open, setOpen] = useState(false);

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
      table.setOpenMenuRowId(nextOpen ? props.rowId : null);
    };

    const { menu, freeformSlot } = useMemo(() => {
      const slots: {
        menu: ReactElement<MenuProps> | null;
        freeformSlot: ReactNode[];
      } = {
        menu: null,
        freeformSlot: [],
      };

      const childrenArray = Children.toArray(props.children);
      for (const child of childrenArray) {
        if (isValidElement<MenuProps>(child) && child.type === Menu) {
          slots.menu = child;
          continue;
        }
        slots.freeformSlot.push(child);
      }
      return slots;
    }, [props.children]);

    if (!menu) {
      return freeformSlot;
    }

    // Avoid spawning the menu while it is closed to avoid react render
    // performance issues.
    if (open === false) {
      return (
        <>
          {freeformSlot}
          <span
            onClick={() => handleOpenChange(true)}
            style={{ display: 'contents' }}
          >
            {getMenuTrigger(menu, density, ariaLabel)}
          </span>
        </>
      );
    }

    return (
      <>
        {freeformSlot}
        <Menu
          open={open}
          onOpenChange={handleOpenChange}
          modal={false}
          {...menu.props}
        >
          <Menu.Trigger>
            <Button variant="default" size={density} aria-label={ariaLabel}>
              <Button.Prefix>
                <DotMenuIcon />
              </Button.Prefix>
            </Button>
          </Menu.Trigger>
          {menu.props.children}
        </Menu>
      </>
    );
  },
);

/**
 * @returns the custom menu trigger if present, or a default menu trigger (dot menu icon button) otherwise.
 */
function getMenuTrigger(
  menu: ReactElement<MenuProps>,
  size?: 'default' | 'condensed',
  ariaLabel?: string,
) {
  const menuChildren = Children.toArray(menu.props.children) as ReactElement[];
  const customMenuTrigger = menuChildren.find(isMenuTrigger);
  if (customMenuTrigger) {
    return customMenuTrigger.props.children;
  }
  return getDefaultMenuTrigger(size, ariaLabel);
}

function getDefaultMenuTrigger(
  size?: 'default' | 'condensed',
  ariaLabel?: string,
): JSX.Element {
  return (
    <Button variant="default" size={size} aria-label={ariaLabel}>
      <Button.Prefix>
        <DotMenuIcon />
      </Button.Prefix>
    </Button>
  );
}

function isFragment(
  child: ReactElement,
): child is ReactElement<PropsWithChildren> {
  return child.type === Fragment;
}

/**
 * Column definition for the Action Column
 * @internal
 */
export const ROW_ACTIONS_COLUMN: ColumnDef<unknown, unknown> = {
  id: DATA_TABLE_ROW_ACTIONS_COLUMN_ID,
  accessorFn: () => '',
  header: '',
  width: 'content',
  enableSorting: false,
  enableUserActions: false,
  enableHiding: false,
  enableResizing: false,
  meta: {
    builtin: true,
    builtinPlacement: 'suffix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
  },
  cell: ({ row, table }) => {
    if (table.options.rowActions === undefined) {
      return null;
    }
    const actions = table.options.rowActions(row.original, {
      rowDensity: table.getVisualVariantOptions().rowDensity,
      rowIndex: row.index,
      rowId: row.id,
    });

    if (isElement(actions)) {
      return (
        <DataTableDefaultCell className={styles.rowActions}>
          <DataTableInternalRowActions rowId={row.id}>
            {isFragment(actions) ? actions.props.children : actions}
          </DataTableInternalRowActions>
        </DataTableDefaultCell>
      );
    }
  },
};
